import { useState, useEffect, useCallback } from "react";

/**
 * Breakpoints alinhados com Tailwind CSS 4:
 *  sm  ≥ 640px
 *  md  ≥ 768px   → tablet começa aqui
 *  lg  ≥ 1024px  → desktop começa aqui
 *  xl  ≥ 1280px
 *  2xl ≥ 1536px
 */
export type ScreenSize = "mobile" | "tablet" | "desktop" | "wide";

/**
 * Alguns celulares Android expõem uma largura CSS acima de 768px mesmo em retrato.
 * Neles, usar apenas a largura produz uma interface de tablet espremida. Todo aparelho
 * com toque, em retrato e abaixo de 1024px recebe a experiência de aplicativo móvel.
 */
export function classifyScreenSize(width: number, height: number, isTouchDevice: boolean): ScreenSize {
  if (isTouchDevice && height > width && width < 1024) return "mobile";
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  if (width < 1536) return "desktop";
  return "wide";
}

export interface Breakpoint {
  /** Largura atual em px */
  width: number;
  /** Altura atual em px */
  height: number;
  /** Categoria de tela */
  screenSize: ScreenSize;
  /** < 768px */
  isMobile: boolean;
  /** 768px – 1023px */
  isTablet: boolean;
  /** ≥ 1024px */
  isDesktop: boolean;
  /** ≥ 1536px */
  isWide: boolean;
  /** Qualquer tela menor que desktop (mobile + tablet) */
  isSmallScreen: boolean;
  /** Orientação do dispositivo */
  isPortrait: boolean;
  isLandscape: boolean;
  /** Dispositivo com toque */
  isTouchDevice: boolean;
}

function getBreakpoint(): Breakpoint {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const screenSize = classifyScreenSize(width, height, isTouchDevice);
  return {
    width,
    height,
    screenSize,
    isMobile: screenSize === "mobile",
    isTablet: screenSize === "tablet",
    isDesktop: screenSize === "desktop" || screenSize === "wide",
    isWide: screenSize === "wide",
    isSmallScreen: screenSize === "mobile" || screenSize === "tablet",
    isPortrait: height > width,
    isLandscape: width >= height,
    isTouchDevice,
  };
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => {
    // SSR-safe: retorna mobile como padrão antes de montar
    if (typeof window === "undefined") {
      return {
        width: 375,
        height: 812,
        screenSize: "mobile",
        isMobile: true,
        isTablet: false,
        isDesktop: false,
        isWide: false,
        isSmallScreen: true,
        isPortrait: true,
        isLandscape: false,
        isTouchDevice: true,
      };
    }
    return getBreakpoint();
  });

  const update = useCallback(() => {
    setBp((prev) => {
      const next = getBreakpoint();
      // Evitar re-render se nada mudou
      if (
        prev.width === next.width &&
        prev.height === next.height
      )
        return prev;
      return next;
    });
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", update, { passive: true });
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [update]);

  return bp;
}

/** Atalho retrocompatível com o hook antigo */
export function useIsMobile(): boolean {
  return useBreakpoint().isMobile;
}
